import { Check } from 'lucide-react'

/**
 * Wizard progress bar for /demande-devis/[1..6] flow.
 * Pass current step (1-based). Shows 4 labeled stages collapsing the 6
 * screens — users don't need 6 visual steps, that signals "too long".
 *
 * Stages:
 *   1-2 Projet    → step 1: describe, step 2: details
 *   3   Lieu      → postal code / address
 *   4-5 Budget    → budget range + urgency
 *   6   Contact   → email / phone submit
 */
interface DevisProgressBarProps {
  current: number // 1-6
}

const STAGES: Array<{ label: string; steps: number[] }> = [
  { label: 'Projet', steps: [1, 2] },
  { label: 'Lieu', steps: [3] },
  { label: 'Budget', steps: [4, 5] },
  { label: 'Contact', steps: [6] },
]

export function DevisProgressBar({ current }: DevisProgressBarProps) {
  const activeStageIdx = STAGES.findIndex((s) => s.steps.includes(current))
  const pctDone = Math.min(100, Math.max(0, ((current - 1) / 5) * 100))

  return (
    <div className="mx-auto max-w-3xl px-4 pt-6">
      <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <span>Étape {current} / 6</span>
        <span>{Math.round(pctDone)}%</span>
      </div>

      {/* Progress track */}
      <div className="relative mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
        <div
          className="h-full bg-gradient-to-r from-primary to-blue-500 transition-all duration-500"
          style={{ width: `${pctDone}%` }}
        />
      </div>

      {/* Stage dots + labels */}
      <div className="mt-3 grid grid-cols-4 gap-1 text-xs">
        {STAGES.map((stage, i) => {
          const done = i < activeStageIdx
          const active = i === activeStageIdx
          return (
            <div key={stage.label} className="flex flex-col items-center gap-1">
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold transition-colors ${
                  done
                    ? 'bg-emerald-500 text-white'
                    : active
                    ? 'bg-primary text-white ring-4 ring-primary/20'
                    : 'bg-slate-200 text-slate-500'
                }`}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </div>
              <span
                className={`text-[11px] font-medium ${
                  active ? 'text-primary' : done ? 'text-emerald-600' : 'text-slate-400'
                }`}
              >
                {stage.label}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
